import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getHotMovies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function HeroBanner() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const hotMovies = getHotMovies();

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % hotMovies.length);
  }, [hotMovies.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + hotMovies.length) % hotMovies.length);
  }, [hotMovies.length]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const currentMovie: Movie = hotMovies[currentIndex];

  if (hotMovies.length === 0) return null;

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center transition-all duration-700"
        style={{ backgroundImage: `url(${currentMovie.bannerImage})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background-50"></div>
      </div>

      <div className="absolute inset-0 flex items-center">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 w-full">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            <div className="w-40 h-56 md:w-52 md:h-72 flex-shrink-0 rounded-lg overflow-hidden animate-scale-in">
              <img
                src={currentMovie.coverImage}
                alt={currentMovie.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex-1 text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start gap-3 mb-3">
                <span className="px-3 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                  {currentMovie.isNew ? '新上线' : '热播中'}
                </span>
                <span className="text-sm text-foreground-400">{currentMovie.year}</span>
                <span className="flex items-center gap-1 text-accent-500 text-sm">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-xs"></i>
                  </div>
                  {currentMovie.rating}
                </span>
              </div>

              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-50 mb-3 animate-fade-in-up">
                {currentMovie.title}
              </h1>

              <p className="text-sm md:text-base text-foreground-300 mb-4 max-w-2xl mx-auto lg:mx-0 line-clamp-2 animate-fade-in-up">
                {currentMovie.description}
              </p>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 mb-6">
                {currentMovie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 bg-background-50/15 text-foreground-200 text-xs rounded-full border border-foreground-300/20 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-center lg:justify-start gap-4">
                <Link
                  to={`/detail/${currentMovie.id}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-play-fill"></i>
                  </div>
                  立即观看
                </Link>
                <button className="inline-flex items-center gap-2 px-6 py-3 bg-background-50/15 text-foreground-200 font-medium rounded-full border border-foreground-300/20 hover:bg-background-50/25 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-add-line"></i>
                  </div>
                  收藏
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={prevSlide}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50/15 text-foreground-200 hover:bg-background-50/30 transition-colors duration-200 backdrop-blur-sm cursor-pointer"
        aria-label="上一个"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50/15 text-foreground-200 hover:bg-background-50/30 transition-colors duration-200 backdrop-blur-sm cursor-pointer"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {hotMovies.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
              index === currentIndex
                ? 'w-6 bg-accent-500'
                : 'bg-foreground-400/40 hover:bg-foreground-400/60'
            }`}
            aria-label={`切换到第${index + 1}个`}
          />
        ))}
      </div>
    </section>
  );
}